import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchFeaturedReviews, imgUrl } from "../api";
import ReviewStars from "./ReviewStars";

/**
 * Homepage reviews strip. Renders nothing when no approved reviews yet,
 * so the homepage stays clean during pre-launch.
 */
const ReviewsStrip = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchFeaturedReviews(6)
      .then((data) => { if (!cancelled) setReviews(data); })
      .catch(() => { if (!cancelled) setReviews([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (reviews.length === 0) return null;

  return (
    <section className="relative overflow-hidden bg-[var(--ivory-soft)] py-24 lg:py-32 vc-grain" data-testid="reviews-strip">
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-20 z-[2]">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 vc-fade-up">
          <div>
            <p className="vc-eyebrow text-[var(--copper)] mb-6">Studio Reviews</p>
            <h2 className="font-serif-display font-light text-4xl md:text-5xl lg:text-6xl leading-[1.08] text-[var(--espresso)] max-w-xl">
              In their own <em className="italic text-[var(--copper)]">rooms</em>.
            </h2>
          </div>
          <p className="text-[var(--espresso)]/60 max-w-sm md:text-right leading-relaxed">
            Short notes from customers who placed these pieces in their homes.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {reviews.map((r, idx) => (
            <Link
              key={r.id}
              to={`/product/${r.product_slug || r.product_id}#reviews`}
              data-testid={`reviews-strip-card-${r.id}`}
              className={`group bg-[var(--cream)] p-7 flex flex-col vc-fade-up vc-stagger-${(idx % 4) + 1}`}
            >
              <ReviewStars value={r.rating} size={14} />
              {r.title && (
                <h3 className="font-serif-display text-xl text-[var(--espresso)] mt-4 leading-snug">{r.title}</h3>
              )}
              <p className="mt-3 text-[var(--espresso)]/75 leading-[1.9] line-clamp-5">{r.text}</p>

              {r.photos && r.photos.length > 0 && (
                <div className="mt-5 grid grid-cols-3 gap-2">
                  {r.photos.slice(0, 3).map((src) => (
                    <div key={src} className="aspect-square bg-[var(--sand)] overflow-hidden">
                      <img src={imgUrl(src)} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-auto pt-6 flex items-center justify-between text-[10px] tracking-[0.28em] uppercase text-[var(--espresso)]/55">
                <span>— {r.name}</span>
                <span className="text-[var(--copper)] group-hover:underline">{r.product_name}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ReviewsStrip;
